import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Form, Button, Input, Info } from 'components/common'
import { Select, Alert } from 'antd'
import { BookingTimeSlotGroupList } from 'pages/admin/products/[id]'
import Modal from 'components/common/modal/modal'
import { ProductVariant } from 'lib/hooks'
import {
  ArrowDownOutlined,
  CaretLeftOutlined,
  CaretRightOutlined,
  DeleteFilled,
  EditFilled,
} from '@ant-design/icons'

type Props = {
  productVariants: ProductVariant[]
  bookingTimeSlotGroupList: BookingTimeSlotGroupList[]
  setBookingTimeSlotGroupList: React.Dispatch<
    React.SetStateAction<BookingTimeSlotGroupList[]>
  >
  // hasChangedList: boolean
}

type Option = {
  value: string
  label: string
  percentageOfProduct: number
}

const BookingTimeSlotGroup: FC<Props> = ({
  productVariants,
  bookingTimeSlotGroupList,
  setBookingTimeSlotGroupList,
  // hasChangedList
}) => {
  // 初期化用にステートを保持する
  const [initialValues, setInitialValues] =
    useState<BookingTimeSlotGroupList[]>()
  // 入力中の関連バリエーション情報
  const [inputRelatedVariants, setInputRelatedVariants] = useState<Option[]>([])
  // モーダルの表示状態
  const [open, setOpen] = useState(false)
  // サンプルのモーダルの表示状態
  const [openSample, setOpenSample] = useState(false)
  // 選択中のサンプル例
  const [currentSample, setCurrentSample] = useState(0)
  // 選択肢
  const [options, setOptions] = useState<Option[]>([])
  // 編集状態
  const [isEdit, setIsEdit] = useState(false)
  // 現在編集中のインデックス
  const [currentEditIndex, setCurrentEditIndex] = useState<number | null>(null)
  // 重複しているvariantsのリスト
  const [duplicateVariants, setDuplicateVariants] = useState<string[]>([])
  // メモ化された関数
  const memoizedGetSelectedVariantList = useCallback(() => {
    return bookingTimeSlotGroupList.flatMap((variants) =>
      variants.map((variant) => variant.id)
    )
  }, [bookingTimeSlotGroupList])

  /**
   * valueから一致するvalueを持つオプションを取得する
   */
  const getOptionByValue = (value: string): Option => {
    return options.find((option) => option.value === value)
  }

  /**
   * グループ設定の更新
   */
  const handleUpdateRelatedVariants = (variantIds: string[]) => {
    // バリエーションのIDと一致する選択肢を取得する
    const newInputRelatedVariants = variantIds.map((id) => getOptionByValue(id))
    // ステートを更新
    setInputRelatedVariants(newInputRelatedVariants)
  }

  /**
   * 占有率の更新
   */
  const handleUpdatePercentageOfProduct = (id: string, value: string) => {
    // ステートをコピー
    const newInputRelatedVariants = [...inputRelatedVariants]
    // 更新対象のバリエーションのインデックスを取得
    const targetIndex = newInputRelatedVariants.findIndex(
      (option) => option.value === id
    )
    // 更新対象のバリエーションの占有率を更新
    newInputRelatedVariants[targetIndex].percentageOfProduct = Number(value)
    // ステートを更新
    setInputRelatedVariants(newInputRelatedVariants)
  }

  /**
   * 更新を押下した時の処理
   */
  const onOk = () => {
    // グループに追加する
    setBookingTimeSlotGroupList((prev) => {
      // 既にグループに属しているバリエーションのリストを作成する
      const newBookingTimeSlotGroupList = [...prev]
      // 編集中の場合は既存のグループを更新
      if (currentEditIndex !== null) {
        newBookingTimeSlotGroupList[currentEditIndex] =
          inputRelatedVariants.map((option) => ({
            id: option.value,
            name: option.label,
            percentageOfProduct: option.percentageOfProduct / 100,
          }))
      } else {
        // 新規追加の場合は新しいグループを追加
        newBookingTimeSlotGroupList.push(
          inputRelatedVariants.map((option) => ({
            id: option.value,
            name: option.label,
            percentageOfProduct: option.percentageOfProduct / 100,
          }))
        )
      }

      return newBookingTimeSlotGroupList
    })
    // 入力中の関連バリエーション情報をリセットする
    setInputRelatedVariants([])
    // モーダルを閉じる
    setOpen(false)
    // 編集インデックスをリセット
    setCurrentEditIndex(null)
  }

  // グループに属するバリエーションのIDリストを作成する
  const getSelectedVariantList = () =>
    bookingTimeSlotGroupList.flatMap((variants) =>
      variants.map((variant) => variant.id)
    )
  // 重複するバリエーションIDリストを作成する
  const findDuplicates = (arr: string[]): string[] => {
    const duplicates = arr.filter((item, index) => arr.indexOf(item) !== index)
    return Array.from(new Set(duplicates))
  }
  // 重複するidのnameリストを作成する
  const findDuplicateNames = (
    bookingList: BookingTimeSlotGroupList[],
    duplicateIds: string[]
  ): string[] => {
    // フラットにして重複するIDに基づいてフィルタリングし、名前を抽出
    const names = bookingList
      .flatMap((group) => group) // 二次元配列をフラットにする
      .filter((item) => duplicateIds.includes(item.id)) // 重複IDに基づいてフィルタリング
      .map((item) => item.name) // 名前を抽出

    // 一意な名前の集合を作成して返す
    return Array.from(new Set(names))
  }

  /**
   * モーダルを開く
   */
  const handleOpen = (index?: number) => {
    // 選択肢を作成する（すべてのバリエーションを含める）
    const createOptions = (selectedVariantList: string[]) => {
      return productVariants.map((data) => ({
        value: data.id,
        label: data.title.jaJp,
        percentageOfProduct: data.percentageOfProduct * 100,
      }))
    }

    // グループに属するバリエーションのIDリストを取得
    let selectedVariantList = getSelectedVariantList()

    if (index !== undefined) {
      // 編集状態をtrueにする
      setIsEdit(true)
      // 編集中のインデックスを設定
      setCurrentEditIndex(index)

      const variants = bookingTimeSlotGroupList[index]
      // 選択中の関連バリエーション情報を更新
      setInputRelatedVariants(
        variants.map((variant) => ({
          value: variant.id,
          label: variant.name,
          percentageOfProduct: variant.percentageOfProduct * 100,
        }))
      )
      const inputRelatedVariantIds = variants.map((variant) => variant.id)
      // 選択中のバリエーションを除く、既にグループに属しているバリエーションのリストを更新
      selectedVariantList = selectedVariantList.filter(
        (id) => !inputRelatedVariantIds.includes(id)
      )
    } else {
      // 編集状態をfalseにする
      setIsEdit(false)
      // 新規追加なのでインデックスはnull
      setCurrentEditIndex(null)
    }

    // 選択肢を作成し、ステートを更新
    setOptions(createOptions(selectedVariantList))

    setOpen(true) // モーダルを開く
  }

  /**
   * モーダルを閉じる
   */
  const handleClose = () => {
    setBookingTimeSlotGroupList(initialValues)
    setOptions([])
    setInputRelatedVariants([])
    setOpen(false)
    // 編集インデックスをリセット
    setCurrentEditIndex(null)
  }

  /**
   * グループの削除
   */
  const handleDeleteGroup = (index: number) => {
    const res = window.confirm(`グループ${index + 1}を削除しますか？`)
    if (!res) return
    setBookingTimeSlotGroupList((prev) => {
      const newBookingTimeSlotGroupList = [...prev]
      newBookingTimeSlotGroupList.splice(index, 1)
      return newBookingTimeSlotGroupList
    })
  }

  // グループの要素数が2以上かつ、占有率が全て入力されているかどうか
  const isDisabled = useMemo(() => {
    if (inputRelatedVariants.length < 2) return true
    return inputRelatedVariants.some(
      (option) =>
        option.percentageOfProduct === 0 || !option.percentageOfProduct
    )
  }, [inputRelatedVariants])

  // 初期化用にステートを保持する
  useEffect(() => {
    setInitialValues(bookingTimeSlotGroupList)
    // グループに属するバリエーションのIDリストを取得
    const selectedVariantList = memoizedGetSelectedVariantList()

    // 重複するバリエーションIDリストを取得する
    const duplicateIds: string[] = findDuplicates(selectedVariantList)
    // 重複するバリエーションNameリストを取得する
    const duplicateNames = findDuplicateNames(
      bookingTimeSlotGroupList,
      duplicateIds
    )
    setDuplicateVariants(duplicateNames)
  }, [bookingTimeSlotGroupList, memoizedGetSelectedVariantList]) // メモ化された関数を依存配列に追加

  return (
    <>
      <Form.Item name="bookingTimeSlotGroup">
        <div className="mb-4">
          <Button type="link" onClick={() => handleOpen()}>
            グループを追加する
          </Button>
        </div>

        <ul className="space-y-4">
          {bookingTimeSlotGroupList?.map((item, index) => (
            <li key={index} className="p-8">
              <div className="flex justify-between items-center mb-4">
                <div className="font-bold">
                  グループ<span className="ml-px">{index + 1}</span>
                </div>
                <div className="space-x-4">
                  <Button
                    className="hover:opacity-80"
                    style={{ borderColor: 'green' }}
                    icon={<EditFilled style={{ color: 'green' }} />}
                    onClick={() => handleOpen(index)}
                  />
                  <Button
                    className="hover:opacity-80"
                    style={{ borderColor: 'red' }}
                    icon={<DeleteFilled style={{ color: 'red' }} />}
                    onClick={() => handleDeleteGroup(index)}
                  />
                </div>
              </div>
              <ul className="flex flex-wrap gap-2 rounded-md border p-2 shadow-sm">
                {item.map((variant, index) => {
                  const colors = [
                    'rgb(243 232 255)',
                    'rgb(254 226 226)',
                    'rgb(220 252 231)',
                    'rgb(219 234 254)',
                    'rgb(254 249 195)',
                    'rgb(252 231 243)',
                  ]
                  const color = colors[index % colors.length]
                  return (
                    <li
                      key={variant.id}
                      style={{ backgroundColor: color }}
                      className="px-8 py-1 rounded-md text-center text-lg"
                    >
                      <div className="font-bold opacity-80">{variant.name}</div>
                      <div>{variant.percentageOfProduct * 100}%</div>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
      </Form.Item>
      {/* 重複バリエーションの警告を表示 */}
      {duplicateVariants && duplicateVariants.length > 0 && (
        <Alert
          message="警告"
          description={
            <>
              <p>重複するバリエーションがあります:</p>
              <ul>
                {duplicateVariants.map((variantName, idx) => (
                  <li key={idx}>{variantName}</li>
                ))}
              </ul>
            </>
          }
          type="warning"
          showIcon
          className="mt-4"
        />
      )}
      {/* グループの追加、編集用モーダル */}
      <Modal
        title={
          <p className="text-3xl font-bold m-0">
            予約枠共有グループの{isEdit ? '編集' : '追加'}
          </p>
        }
        width={'50%'}
        open={open}
        onOk={onOk}
        onCancel={handleClose}
        bodyStyle={{ paddingTop: '16px', paddingBottom: '16px' }}
        footer={
          <div className="flex justify-end items-center">
            <Button onClick={handleClose} style={{ marginRight: '16px' }}>
              キャンセル
            </Button>
            <Button onClick={onOk} type="primary" disabled={isDisabled}>
              {isEdit ? '更新' : '追加'}
            </Button>
          </div>
        }
      >
        <div className="mb-8 p-4 rounded-md bg-blue-100">
          予約枠を共有するバリエーションのグループを設定できます。
          <br />
          グループ化されたバリエーションは1つの予約枠を共有し、
          各バリエーションに設定された予約枠占有率に基づいて予約可能かどうかが決定されます。
          <Button
            style={{ marginLeft: 'auto' }}
            type="link"
            onClick={() => setOpenSample(true)}
          >
            使用例を確認
          </Button>
        </div>
        <div className="space-y-12">
          <div>
            <div className="font-bold mb-1">グループ設定</div>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              options={options}
              value={inputRelatedVariants.map((option) => option.value)}
              onChange={handleUpdateRelatedVariants}
            />
          </div>
          <div>
            <div className="font-bold mb-1">
              <Info title="グループの予約枠（100%）に対する占有率の設定項目です。">
                予約枠占有率の設定
              </Info>
            </div>
            <ul className="space-y-4">
              {inputRelatedVariants?.map((variant) => (
                <li
                  key={variant.value}
                  className="grid grid-cols-2 items-center"
                >
                  <div className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                    {variant.label}
                  </div>
                  <Input
                    suffix={'%'}
                    type="number"
                    min={0}
                    max={100}
                    defaultValue={variant.percentageOfProduct ?? null}
                    value={variant.percentageOfProduct}
                    onChange={(e) =>
                      handleUpdatePercentageOfProduct(
                        variant.value,
                        e.target.value
                      )
                    }
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>
      {/* 使用例のモーダル */}
      <Modal
        title={
          <p className="text-3xl font-bold m-0 flex items-center">
            {currentSample === 1 && (
              <CaretLeftOutlined
                style={{ fontSize: '24px', marginRight: '32px' }}
                onClick={() => setCurrentSample(0)}
              />
            )}
            使用例（{currentSample === 0 ? '大競技場' : 'ボールフィールド'}）
            {currentSample === 0 && (
              <CaretRightOutlined
                style={{ fontSize: '24px', marginLeft: '32px' }}
                onClick={() => setCurrentSample(1)}
              />
            )}
          </p>
        }
        width={'50%'}
        open={openSample}
        onCancel={() => setOpenSample(false)}
        bodyStyle={{ paddingTop: '16px', paddingBottom: '16px' }}
        footer={null}
      >
        <div className="space-y-12 mb-4">
          <div>
            <div className="font-bold mb-1">グループ設定</div>
            <Select
              mode="multiple"
              style={{ width: '100%' }}
              options={sampleOptions[currentSample]}
              value={sampleOptions[currentSample].map((option) => option.value)}
            />
          </div>
          <div>
            <div className="font-bold mb-1">
              <Info title="グループの予約枠（100%）に対する占有率の設定項目です。">
                予約枠占有率の設定
              </Info>
            </div>
            <ul className="space-y-4">
              {sampleOptions[currentSample]?.map((item, index) => (
                <li key={index} className="grid grid-cols-2 items-center">
                  <div className="overflow-hidden overflow-ellipsis whitespace-nowrap">
                    {item.label}
                  </div>
                  <Input
                    suffix={'%'}
                    type="number"
                    min={0}
                    max={100}
                    value={item.percentageOfProduct}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="text-center mb-4">
          <ArrowDownOutlined style={{ fontSize: '32px' }} />
        </div>
        <div className="p-4 rounded-md border-dotted border-4 border-blue-200  grid grid-cols-2 gap-y-6">
          <div className="font-bold">予約されているバリエーション</div>
          <div className="font-bold">他バリエーションの予約可否</div>
          {sampleBookingInfo[currentSample]?.map((item, index) => (
            <React.Fragment key={index}>
              <div>{item.name}</div>
              <div>
                予約可：{item.status.ok}
                <br />
                予約不可：{item.status.not}
              </div>
            </React.Fragment>
          ))}
        </div>
      </Modal>
    </>
  )
}

export default BookingTimeSlotGroup

// サンプルモーダルに表示する選択肢
const sampleOptions = [
  [
    {
      value: '1',
      label: '全面',
      percentageOfProduct: 100,
    },
    {
      value: '2',
      label: '1/2面',
      percentageOfProduct: 50,
    },
    {
      value: '3',
      label: '1/3面',
      percentageOfProduct: 33,
    },
    {
      value: '4',
      label: '2/3面',
      percentageOfProduct: 66,
    },
  ],
  [
    {
      value: '5',
      label: 'ボールフィールド北１面',
      percentageOfProduct: 100,
    },
    {
      value: '6',
      label: 'ボールフィールド北半面A',
      percentageOfProduct: 50,
    },
    {
      value: '7',
      label: 'ボールフィールド北半面B',
      percentageOfProduct: 50,
    },
  ],
]

// サンプルモーダルに表示する予約情報
const sampleBookingInfo = [
  [
    { name: '全面', status: { not: '全面、1/2面、1/3面、2/3面', ok: 'なし' } },
    { name: '1/2面', status: { not: '全面、 2/3面', ok: '1/2面、1/3面' } },
    { name: '1/3面', status: { not: '全面', ok: '1/2面、1/3面、2/3面' } },
    { name: '2/3面', status: { not: '全面、1/2面、2/3面', ok: '1/3面' } },
  ],
  [
    {
      name: 'ボールフィールド北１面',
      status: {
        not: 'ボールフィールド北１面、ボールフィールド北半面A、ボールフィールド北半面B',
        ok: 'なし',
      },
    },
    {
      name: 'ボールフィールド北半面A',
      status: {
        not: 'ボールフィールド北１面',
        ok: 'ボールフィールド北半面A、ボールフィールド北半面B',
      },
    },
    {
      name: 'ボールフィールド北半面B',
      status: {
        not: 'ボールフィールド北１面',
        ok: 'ボールフィールド北半面A、ボールフィールド北半面B',
      },
    },
  ],
]
